import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, RefreshCw, Trash2, Rss, Plus } from "lucide-react";

const SOURCES = ["Booking.com", "Airbnb", "VRBO", "Other"];

export default function ChannelsPage() {
  const [feeds, setFeeds] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [today, setToday] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ source: "Booking.com", url: "", room: "" });
  const [adding, setAdding] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const load = () => Promise.all([
    api.get("/channels").then((r) => setFeeds(r.data)),
    api.get("/channel-bookings").then((r) => setBookings(r.data)),
    api.get("/channel-bookings/today").then((r) => setToday(r.data)),
  ]).finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const addFeed = async () => {
    if (!form.url.trim()) { toast.error("Paste the iCal URL"); return; }
    setAdding(true);
    try {
      const { data } = await api.post("/channels", form);
      if (data.sync?.ok) toast.success(`Added — pulled ${data.sync.count} reservation(s)`);
      else toast.error(data.sync?.error || "Added, but the feed could not be read");
      setForm({ source: "Booking.com", url: "", room: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add feed"); }
    finally { setAdding(false); }
  };

  const syncOne = async (fid) => { try { const { data } = await api.post(`/channels/${fid}/sync`); data.ok ? toast.success(`Pulled ${data.count} reservation(s)`) : toast.error(data.error || "Sync failed"); load(); } catch { toast.error("Sync failed"); } };
  const syncAll = async () => { setSyncing(true); try { const { data } = await api.post("/channels/sync"); toast.success(`Synced ${data.count} reservation(s)`); load(); } catch { toast.error("Sync failed"); } finally { setSyncing(false); } };
  const removeFeed = async (fid) => { try { await api.delete(`/channels/${fid}`); toast.success("Feed removed"); load(); } catch { toast.error("Could not remove"); } };

  const fmt = (d) => d ? new Date(d).toLocaleDateString() : "—";

  if (loading) return <Loader2 className="w-5 h-5 animate-spin text-accent" />;

  return (
    <div className="space-y-8" data-testid="channels-page">
      <div>
        <h1 className="font-serif text-3xl text-foreground">Channel bookings</h1>
        <p className="text-muted-foreground text-sm mt-1">Pull reservations from Booking.com, Airbnb, VRBO and other platforms via their iCal export links. Rooms are blocked automatically; guest details may be limited by the channel.</p>
      </div>

      {today && (
        <div className="grid md:grid-cols-2 gap-4" data-testid="today-panel">
          {[["Arrivals today", today.arrivals], ["Departures today", today.departures]].map(([label, list]) => (
            <div key={label} className="bg-card border border-border rounded-md p-5">
              <h3 className="font-serif text-lg text-foreground mb-3">{label} ({list.length})</h3>
              {list.length === 0 ? <p className="text-sm text-muted-foreground">Nothing scheduled.</p> : (
                <div className="space-y-2">
                  {list.map((b) => (
                    <div key={b.id} className="flex items-center justify-between text-sm" data-testid={`today-${b.id}`}>
                      <span className="text-foreground truncate mr-2">{b.room || b.summary || "Reservation"}</span>
                      <Badge className="bg-secondary text-foreground border-0 shrink-0">{b.source}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="bg-card border border-border rounded-md p-6 space-y-4">
        <h3 className="font-serif text-lg text-foreground">Connect a calendar</h3>
        <div className="grid sm:grid-cols-4 gap-4 items-end">
          <div>
            <Label>Channel</Label>
            <Select value={form.source} onValueChange={(v) => setForm({ ...form, source: v })}>
              <SelectTrigger className="mt-1.5" data-testid="channel-source"><SelectValue /></SelectTrigger>
              <SelectContent>{SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2">
            <Label>iCal export URL</Label>
            <Input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://…/calendar.ics" className="mt-1.5" data-testid="channel-url" />
          </div>
          <div>
            <Label>Room label (optional)</Label>
            <Input value={form.room} onChange={(e) => setForm({ ...form, room: e.target.value })} placeholder="e.g. Suite 1" className="mt-1.5" data-testid="channel-room" />
          </div>
        </div>
        <p className="text-xs text-muted-foreground">Find the iCal link in the channel: Airbnb → Calendar → Availability → Export Calendar; Booking.com → Calendar → Sync calendars → Export.</p>
        <Button onClick={addFeed} disabled={adding} className="gap-1.5" data-testid="channel-add-btn">{adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add feed</Button>
      </div>

      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-serif text-lg text-foreground">Connected calendars ({feeds.length})</h3>
          {feeds.length > 0 && <Button size="sm" variant="outline" onClick={syncAll} disabled={syncing} className="gap-1.5" data-testid="channel-sync-all-btn">{syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Sync all</Button>}
        </div>
        {feeds.length === 0 ? (
          <div className="text-center py-12 border border-dashed border-border rounded-md"><Rss className="w-8 h-8 text-muted-foreground mx-auto mb-2" /><p className="text-sm text-muted-foreground">No channels connected yet.</p></div>
        ) : (
          <div className="bg-card border border-border rounded-md divide-y divide-border">
            {feeds.map((f) => (
              <div key={f.id} className="p-4 flex items-center justify-between gap-4" data-testid={`channel-feed-${f.id}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <Badge className="bg-secondary text-foreground border-0">{f.source}</Badge>
                    {f.room && <span className="text-sm text-muted-foreground">{f.room}</span>}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1 truncate max-w-md">{f.url}</p>
                  <p className="text-xs mt-0.5">
                    {f.last_error
                      ? <span className="text-red-600">Error: {f.last_error}</span>
                      : <span className="text-muted-foreground">{f.last_synced ? `Last synced ${new Date(f.last_synced).toLocaleString()} · ${f.last_count} reservation(s)` : "Not synced yet"}</span>}
                  </p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button size="sm" variant="outline" onClick={() => syncOne(f.id)} data-testid={`channel-sync-${f.id}`}><RefreshCw className="w-3.5 h-3.5" /></Button>
                  <Button size="sm" variant="outline" onClick={() => removeFeed(f.id)} className="text-red-600" data-testid={`channel-remove-${f.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <h3 className="font-serif text-lg text-foreground mb-3">Reservations ({bookings.length})</h3>
        {bookings.length === 0 ? (
          <div className="text-center py-12 border border-dashed border-border rounded-md"><p className="text-sm text-muted-foreground">No channel reservations yet.</p></div>
        ) : (
          <div className="overflow-x-auto bg-card border border-border rounded-md">
            <table className="w-full text-sm" data-testid="channel-bookings-table">
              <thead className="text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                <tr><th className="text-left font-medium px-4 py-3">Source</th><th className="text-left font-medium px-4 py-3">Room</th><th className="text-left font-medium px-4 py-3">Arrival</th><th className="text-left font-medium px-4 py-3">Departure</th><th className="text-left font-medium px-4 py-3">Details</th></tr>
              </thead>
              <tbody className="divide-y divide-border">
                {bookings.map((b) => (
                  <tr key={b.id} data-testid={`channel-booking-${b.id}`}>
                    <td className="px-4 py-3"><Badge className="bg-secondary text-foreground border-0">{b.source}</Badge></td>
                    <td className="px-4 py-3 text-muted-foreground">{b.room || "—"}</td>
                    <td className="px-4 py-3 text-foreground whitespace-nowrap">{fmt(b.start)}</td>
                    <td className="px-4 py-3 text-foreground whitespace-nowrap">{fmt(b.end)}</td>
                    <td className="px-4 py-3 text-muted-foreground truncate max-w-xs">{b.summary || b.guest_hint || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
